import type { TFunction } from "i18next";
import type { ClientPortalSubcampaign } from "../../store/apis/clientPortalApi";
import type { StatItem } from "./StickyStatsViewClient";
import {
  costPer,
  formatMoney,
  formatPercent,
  formatUnitMoney,
  isPerThousand,
} from "./clientFormatViewClient";

// Dinero de la vista de cliente: siempre lo facturado al cliente (con margen),
// nunca el coste real de plataforma. Inversión, presupuesto y costes por unidad.

export interface MoneyTotals {
  investment: number;
  /** Presupuesto (con margen). null si no hay */
  budget: number | null;
  impressions: number;
  clicks: number;
  completeViews: number;
  conversions: number;
  engagements: number;
}

/** Modelo de compra de una sola subcampaña: su coste por unidad pactado y el conseguido. */
export interface MoneyPricing {
  model: ClientPortalSubcampaign["pricingModel"];
  agreedUnitCost: number | null;
  unitCost: number | null;
}

/** Totales de dinero de varias subcampañas. Solo hay presupuesto si lo tienen todas. */
export const moneyTotals = (subs: ClientPortalSubcampaign[]): MoneyTotals => ({
  investment: subs.reduce((sum, s) => sum + s.investment, 0),
  budget:
    subs.length > 0 && subs.every((s) => s.budget)
      ? subs.reduce((sum, s) => sum + (s.budget ?? 0), 0)
      : null,
  impressions: subs.reduce((sum, s) => sum + s.impressions, 0),
  clicks: subs.reduce((sum, s) => sum + s.clicks, 0),
  completeViews: subs.reduce((sum, s) => sum + s.completeViews, 0),
  conversions: subs.reduce((sum, s) => sum + s.conversions, 0),
  engagements: subs.reduce((sum, s) => sum + s.engagements, 0),
});

// Qué coste por unidad repite cada modelo de compra (no se enseña dos veces).
const SAME_AS_MODEL: Record<string, string> = {
  CPM: "cpm",
  CPC: "cpc",
  CPV: "cpv",
  CPA: "cpa",
  CPL: "cpa",
  CPE: "cpe",
};

/**
 * Cifras de dinero: inversión, cuánto lleva del presupuesto, el coste por
 * unidad frente al pactado (en una subcampaña) y los costes por mil
 * impresiones, clic, vista, conversión e interacción que tengan sentido.
 */
export const moneyStatItems = (
  totals: MoneyTotals,
  currency: string,
  t: TFunction,
  language: string,
  pricing?: MoneyPricing
): StatItem[] => {
  const money = (v: number) => formatMoney(v, currency, language);
  const unit = (v: number) => formatUnitMoney(v, currency, language);
  const { investment, budget } = totals;
  const repeated = pricing?.model ? SAME_AS_MODEL[pricing.model] : undefined;

  const unitCosts = (
    [
      ["cpm", costPer(investment, totals.impressions, true)],
      ["cpc", costPer(investment, totals.clicks)],
      ["cpv", costPer(investment, totals.completeViews)],
      ["cpa", costPer(investment, totals.conversions)],
      ["cpe", costPer(investment, totals.engagements)],
    ] as [string, number | null][]
  ).filter(([key, value]) => value !== null && key !== repeated);

  return [
    {
      key: "investment",
      label: t("clientView.stats.investment"),
      value: money(investment),
      hint: budget
        ? t("clientView.money.ofBudget", { budget: money(budget) })
        : undefined,
    },
    ...(budget
      ? [
          {
            key: "budgetSpent",
            label: t("clientView.money.budgetSpent"),
            value: formatPercent(investment / budget, language),
            hint: t("clientView.money.budgetLeft", {
              amount: money(Math.max(budget - investment, 0)),
            }),
          },
        ]
      : []),
    ...(pricing?.model && pricing.unitCost !== null
      ? [
          {
            key: "unitCost",
            label: t("clientView.money.actualUnitCost", {
              model: pricing.model,
            }),
            value: unit(pricing.unitCost),
            hint: pricing.agreedUnitCost
              ? t(
                  isPerThousand(pricing.model)
                    ? "clientView.money.agreedPerThousand"
                    : "clientView.money.agreed",
                  { value: unit(pricing.agreedUnitCost) }
                )
              : undefined,
          },
        ]
      : []),
    ...unitCosts.map(([key, value]) => ({
      key,
      label: t(`clientView.money.${key}`),
      value: unit(value as number),
      hint: t(`clientView.money.${key}Hint`),
    })),
  ];
};

/** Las cifras de dinero en dos: inversión y presupuesto, y costes por unidad. */
export const splitMoneyItems = (
  items: StatItem[]
): { budget: StatItem[]; costs: StatItem[] } => {
  const isBudget = (item: StatItem) =>
    item.key === "investment" || item.key === "budgetSpent";
  return {
    budget: items.filter(isBudget),
    costs: items.filter((item) => !isBudget(item)),
  };
};
